import React from "react";
import { Link } from "react-router-dom";
import { User, Radio, FileSignature, RefreshCcw, CalendarDays, MessageSquare, Mic, Bell, ExternalLink } from "lucide-react";

const DAY = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export function Bullet({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-9 h-9 shrink-0 border border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-red)]"><Icon size={16} /></div>
      <div>
        <div className="font-display text-base">{title}</div>
        <div className="text-sm text-[var(--ff-text-dim)] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export function WelcomeVisual({ name }) {
  return (
    <div className="relative h-full min-h-[260px] bg-[var(--ff-red)] p-8 flex flex-col justify-end overflow-hidden" data-testid="onb-welcome-visual">
      <div className="absolute -top-10 -right-6 font-display text-[11rem] leading-none text-black/15 select-none">FF</div>
      <div className="label-eyebrow text-white/80 mb-2">▮ NOW ON AIR</div>
      <div className="font-display text-4xl leading-none">{(name || "NEW HOST").toUpperCase()}</div>
      <div className="font-mono-ui text-xs mt-2 text-white/80">Freshy Fresh Productions · Host</div>
    </div>
  );
}

export function DashboardVisual() {
  return (
    <div className="border border-[var(--ff-border)] bg-[var(--ff-bg)] p-5 space-y-4" data-testid="onb-dashboard-visual">
      <div className="label-eyebrow text-[var(--ff-red)]">▮ HOST STUDIO</div>
      <div className="flex gap-px bg-[var(--ff-border)] w-fit">
        {["Bio & Payouts", "Donation Alerts", "Contracts"].map((t, i) => (
          <span key={t} className={`px-3 py-1.5 font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] ${i === 0 ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)]"}`}>{t}</span>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2 space-y-2">
          <div className="h-3 w-24 bg-[var(--ff-border)]" />
          <div className="h-8 bg-[var(--ff-surface)] border border-[var(--ff-border)]" />
          <div className="h-16 bg-[var(--ff-surface)] border border-[var(--ff-border)]" />
        </div>
        <div className="aspect-square bg-[var(--ff-red)]/80 flex items-center justify-center"><User size={28} /></div>
      </div>
    </div>
  );
}

export const DASHBOARD_POINTS = [
  { icon: User, title: "Bio & Payouts", text: "Edit your public title, bio, headshot and social link. Add your PayPal email and connect Stripe to get paid your host cut." },
  { icon: Radio, title: "Donation Alerts", text: "Grab your OBS overlay link, approve or reject fan donations, and replay alerts live on stream." },
  { icon: FileSignature, title: "Contracts", text: "Confidential agreements from management show up here for you to review and e-sign." },
  { icon: RefreshCcw, title: "Sync Discord Roles", text: "Just got a new role in Discord? Hit Sync so the site picks it up instantly." },
];

export function ScheduleVisual({ shows }) {
  const days = new Set(shows.flatMap((s) => (s.days_of_week?.length ? s.days_of_week : [s.day_of_week ?? 0])));
  return (
    <div className="border border-[var(--ff-border)] bg-[var(--ff-bg)] p-5 space-y-4" data-testid="onb-schedule-visual">
      <div className="grid grid-cols-7 gap-px bg-[var(--ff-border)]">
        {DAY.map((d, i) => (
          <div key={d} className={`py-3 text-center font-mono-ui text-[0.6rem] tracking-[0.12em] ${days.has(i) ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)]"}`}>{d}</div>
        ))}
      </div>
      {shows.length === 0 ? (
        <p className="text-xs text-[var(--ff-text-dim)]" data-testid="onb-no-shows">No shows assigned to you yet — once an admin adds you to a show, it appears here and on the public Schedule.</p>
      ) : (
        <ul className="space-y-2" data-testid="onb-my-shows">
          {shows.map((s) => (
            <li key={s.id} className="border-l-2 border-[var(--ff-red)] pl-3">
              <div className="font-display">{s.title}</div>
              <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.12em]">
                {(s.days_of_week?.length ? s.days_of_week : [s.day_of_week ?? 0]).map((d) => DAY[d]).join(" · ")} / {s.start_time}–{s.end_time}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Link to="/schedule" target="_blank" className="btn-ghost inline-flex items-center gap-1.5 text-[0.65rem] !px-3 !py-1.5 w-fit" data-testid="onb-open-schedule"><ExternalLink size={11} /> Open Full Schedule</Link>
    </div>
  );
}

export const SCHEDULE_POINTS = [
  { icon: CalendarDays, title: "The public Schedule", text: "Every show airs on the network schedule. Fans can follow shows, switch to their timezone and get reminders." },
  { icon: Bell, title: "Now Live banner", text: "When your show is live, the site shows a Now Live banner and fans who follow you get notified." },
];

export const DISCORD_POINTS = [
  { icon: MessageSquare, title: "Keep your DMs open", text: "The Freshy Fresh bot DMs you urgent notices, show reminders and contracts to sign." },
  { icon: Mic, title: "Live voice alerts", text: "Donations to you are announced in the On Air voice channel with text-to-speech while you're live." },
  { icon: FileSignature, title: "Policies come first", text: "Company policies can change. When a new one is added you'll need to read it before getting back into Host Studio." },
];
